import { useParams } from 'react-router';
import { type ErrorLike } from '@apollo/client';
import { useSuspenseQuery } from '@apollo/client/react';
import { useState } from 'react';

import { SaasEscrowDocument } from 'src/graphql/actions/saasEscrow/saasEscrow.query';

import { createRequiredContext } from '../createRequiredContext';

import type { ISaasEscrowQuery } from 'src/graphql/actions/saasEscrow/saasEscrow.query';

function assertDataExists(
  error: ErrorLike | undefined,
  data: ISaasEscrowQuery | undefined,
): asserts data is ISaasEscrowQuery & {
  saasEscrow: NonNullable<ISaasEscrowQuery['saasEscrow']>;
} {
  if (error) throw error;
  if (!data?.saasEscrow) throw new Error('Saas escrow data not found');
}

type IUiState = {
  isTerminationDialogOpen: boolean;
};

const [useSaasEscrow, SaasEscrowProvider] = createRequiredContext<{
  saasEscrow: NonNullable<ISaasEscrowQuery['saasEscrow']>;
  error?: ErrorLike;
  ui: IUiState;
  refetch: () => void;
  setIsTerminationDialogOpen: (isOpen: boolean) => void;
}>();

const SaasEscrowContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [isTerminationDialogOpen, setIsTerminationDialogOpen] = useState(false);
  const { id: saasEscrowId = '' } = useParams();
  const { data, error, refetch } = useSuspenseQuery(SaasEscrowDocument, {
    variables: { saasEscrowId },
    skip: !saasEscrowId,
  });

  assertDataExists(error, data);

  const providerValue = {
    saasEscrow: data?.saasEscrow,
    error,
    ui: {
      isTerminationDialogOpen,
    },
    refetch,
    setIsTerminationDialogOpen,
  };

  return <SaasEscrowProvider value={providerValue}>{children}</SaasEscrowProvider>;
};

export { useSaasEscrow, SaasEscrowContextProvider };
